import React, { useCallback, useContext, useEffect, useState } from 'react';
import { AlertOutlined, EllipsisOutlined, ExclamationCircleOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Dropdown, Layout, Menu, Modal, PageHeader, Table, Typography } from 'antd';
import { apiClient, apiErrorMessage, hasPermission } from '../../utils';
import { Ewo, User } from '../../models';
import { UserContext } from '../contexts';
import { EwoEditPage } from './ewo-edit-page';
import moment from 'moment';
import { EwoDetailPage } from './ewo-detail-page';

export function EwosPage() {
  const { Content } = Layout;
  const { Text } = Typography;

  const { user } = useContext(UserContext);

  const [loading, setLoading] = useState(false);
  const [ewos, setEwos] = useState<Ewo[]>();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editModalEwo, setEditModalEwo] = useState<Ewo>();
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailModalEwo, setDetailModalEwo] = useState<Ewo>();
  const [canEditAll, setCanEditAll] = useState(false);

  useEffect(() => {
    setCanEditAll(hasPermission(user, 'ewos.edit'));
  }, [user]);

  const loadEwos = useCallback(() => {
    setLoading(true);
    apiClient
      .get(`ewos?userId=${user?.id}`)
      .then(response => {
        setEwos(response.data);
        setLoading(false);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
        setLoading(false);
      });
  }, [user?.id]);

  useEffect(() => {
    loadEwos();
  }, [loadEwos]);

  const onRefreshClick = () => {
    loadEwos();
  };

  const onEwoDetailSaved = () => {
    loadEwos();
  };

  const onEwoEditSaved = () => {
    closeEditModal();
    loadEwos();
  };

  const openEditModal = () => {
    setEditModalEwo(undefined);
    setEditModalOpen(true);
  };

  const openEditModalByEwo = (ewo: Ewo) => {
    setEditModalEwo(ewo);
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditModalEwo(undefined);
  };

  const openDetailModalByEwo = (ewo: Ewo) => {
    setDetailModalEwo(ewo);
    setDetailModalOpen(true);
  };

  const closeDetailModal = () => {
    setDetailModalOpen(false);
    setDetailModalEwo(undefined);
  };

  const deleteEwo = (id: string) => {
    setLoading(true);
    apiClient
      .delete(`ewos/${id}`)
      .then(() => {
        setLoading(false);
        loadEwos();
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  };

  const confirmDeleteEwo = (id: string) => {
    const onOk = () => deleteEwo(id);
    Modal.confirm({
      title: 'EWO sil',
      icon: <ExclamationCircleOutlined />,
      content: "EWO'yu silmek istediğinize emin misiniz?",
      okText: 'Evet',
      cancelText: 'Vazgeç',
      onOk: onOk,
    });
  };

  const onTableActionClick = (e: any, id: string) => {
    if (e.key === 'details') {
      const ewo = ewos?.find(e => e.id === id);
      if (ewo) {
        openDetailModalByEwo(ewo);
      }
    }
    if (e.key === 'edit') {
      const ewo = ewos?.find(m => m.id === id);
      if (ewo) {
        openEditModalByEwo(ewo);
      } else {
        openEditModal();
      }
    } else if (e.key === 'del') {
      confirmDeleteEwo(id);
    }
  };

  const tableActions = (id: string) => (
    <Menu onClick={(e: any) => onTableActionClick(e, id)}>
      <Menu.Item key="details">
        <Text>Detaylar</Text>
      </Menu.Item>
      {canEditAll && (
        <>
          <Menu.Item key="edit">
            <Text>Düzenle</Text>
          </Menu.Item>
          <Menu.Item key="del">
            <Text type="danger">Sil</Text>
          </Menu.Item>
        </>
      )}
    </Menu>
  );

  const tableColumns: any[] = [
    {
      title: 'EWO no',
      dataIndex: 'ewoNo',
      key: 'ewoNo',
      render: (ewoNo: string, ewo: Ewo) => (
        <Button type="link" onClick={() => openDetailModalByEwo(ewo)}>
          {ewoNo}
        </Button>
      ),
    },
    {
      title: 'Ne',
      dataIndex: 'what',
      key: 'what',
    },
    {
      title: 'Ne zaman',
      dataIndex: 'when',
      key: 'when',
    },
    {
      title: 'Tarih / saat',
      dataIndex: 'occuredAt',
      key: 'occuredAt',
      render: (occuredAt: string) => moment(occuredAt).format('DD.MM.yyyy HH:mm'),
    },
    {
      title: 'Nerede',
      dataIndex: 'where',
      key: 'where',
    },
    {
      title: 'Kim',
      dataIndex: ['who', 'fullNameId'],
      key: 'who',
    },
    {
      title: 'Rutin çalışma',
      dataIndex: 'routineWork',
      key: 'routineWork',
      render: (routineWork: boolean) => (routineWork ? 'Evet' : 'Hayır'),
    },
    {
      title: 'Hangi',
      dataIndex: 'which',
      key: 'which',
    },
    {
      title: 'Nasıl',
      dataIndex: 'howDescription',
      key: 'howDescription',
    },
    {
      title: 'Oluşturma',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt: string) => moment(createdAt).format('DD.MM.yyyy HH:mm'),
    },
    {
      title: 'Oluşturan',
      dataIndex: 'createdBy',
      key: 'createdBy',
      render: (createdBy: User) => createdBy.fullNameId,
    },
    {
      title: '',
      key: 'action',
      render: (_: any, ewo: Ewo) => (
        <div style={{ textAlign: 'right' }}>
          <Dropdown overlay={tableActions(ewo.id)} trigger={['click']}>
            <Button size="small" icon={<EllipsisOutlined />} />
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={
          <>
            <AlertOutlined /> EWO'lar
          </>
        }
        extra={[
          <Button key="refresh" onClick={onRefreshClick} icon={<ReloadOutlined />}>
            Yenile
          </Button>,
          <Button key="add" onClick={openEditModal} icon={<PlusOutlined />} type="primary">
            EWO ekle
          </Button>,
        ]}
      />
      <Content>
        <Table columns={tableColumns} dataSource={ewos} loading={loading} pagination={false} />
      </Content>
      <Modal title="EWO" visible={detailModalOpen} width={'50%'} footer={null} onCancel={closeDetailModal} destroyOnClose={true}>
        <EwoDetailPage ewo={detailModalEwo} onSaved={onEwoDetailSaved} />
      </Modal>
      <Modal title="EWO" visible={editModalOpen} footer={null} onCancel={closeEditModal} destroyOnClose={true}>
        <EwoEditPage ewo={editModalEwo} onSaved={onEwoEditSaved} />
      </Modal>
    </>
  );
}
